(() => {
  const GIFT_PROPERTY = '_aerre_threshold_gift';
  const TIER_PROPERTY = '_tier_discount_eligible';
  const CHECKOUT_DEADLINE_MS = 3000;
  const CHECKOUT_LOG_KEY = 'aerre:checkout-handoff:v1';
  const GIFT_PENDING_KEY = 'aerre:threshold-gift-pending:v1';
  const GIFT_PREFERENCE_ATTRIBUTE = '_aerre_gwp_preference_v2';
  const GIFT_FOLLOWUP_ATTRIBUTE = '__aerre_gwp_fulfilment_check';
  const GIFT_INTERACTION_MS = 30000;
  const RETIRED_GIFT_MARKER = 'spend_rewards_20261019';
  const RETIRED_ATTRIBUTES = ['aerre_three_way_mode_20261021', 'aerre_spend_price_cohort_20261019', 'aerre_spend_rewards_20261019', 'aerre_spend_cash_20261019', 'aerre_cash_30', 'aerre_cash_50'];

  function retiredGiftItems(state) {
    const flatten = items => items.flatMap(item => [item, ...flatten(item.item_components || [])]);
    return flatten(state.cart?.items || []).filter(item => item.properties?.[GIFT_PROPERTY] === RETIRED_GIFT_MARKER);
  }

  function legacyTransitionAttributes(state) {
    const current = state.cart?.attributes || {};
    const changes = {};
    for (const key of RETIRED_ATTRIBUTES) {
      if (current[key] != null && current[key] !== '') changes[key] = '';
    }
    if (['high', RETIRED_GIFT_MARKER].includes(current.perfume50_offer_74366e03)) {
      changes.perfume50_offer_74366e03 = 'control';
    }
    return changes;
  }

  function needsLegacyTransition(state) {
    return retiredGiftItems(state).length > 0 || Object.keys(legacyTransitionAttributes(state)).length > 0;
  }

  function priorityLines(state, config) {
    return (state.cart?.items || []).filter(item => Number(item.product_id) === Number(config.priority?.productId));
  }

  function priorityUpdates(state, config, selected) {
    let kept = false;
    return Object.fromEntries(priorityLines(state, config).flatMap(item => {
      const quantity = selected && !kept && Number(item.variant_id) === Number(config.priority.variantId) ? 1 : 0;
      if (quantity) kept = true;
      return Number(item.quantity) === quantity ? [] : [[item.key, quantity]];
    }));
  }

  function tierPropertyChanges(state) {
    if (!Array.isArray(state?.tierLines)) return [];
    const eligibility = new Map(state.tierLines.map(line => [line.key, line.eligible === true]));
    const items = state.cart?.items || [];
    const children = new Set(items.flatMap(item => (item.item_components || []).map(component => component.key)));
    return items.flatMap(item => {
      if (!eligibility.has(item.key) || children.has(item.key) || item.parent_relationship) return [];
      const properties = { ...(item.properties || {}) };
      const desired = eligibility.get(item.key);
      if (desired && properties[TIER_PROPERTY] !== 'true') {
        properties[TIER_PROPERTY] = 'true';
      } else if (!desired && properties[TIER_PROPERTY] != null && properties[TIER_PROPERTY] !== '') {
        delete properties[TIER_PROPERTY];
        if (!Object.keys(properties).length) properties[TIER_PROPERTY] = '';
      } else return [];
      return [{ id: item.key, quantity: item.quantity, properties }];
    });
  }

  function propertiesMatch(left, right) {
    const entries = value => Object.entries(value || {}).filter(([, entry]) => entry != null && entry !== '').map(([key, entry]) => [key, String(entry)]).sort(([a], [b]) => a.localeCompare(b));
    return JSON.stringify(entries(left)) === JSON.stringify(entries(right));
  }

  function giftLines(state, marker) {
    const items = state.cart?.items || [];
    const componentKeys = new Set(items.flatMap((item) => (item.item_components || []).map((component) => component.key)));
    const marked = items.filter((item) => item.properties?.[GIFT_PROPERTY] === marker && !item.item_components?.length && !item.parent_relationship && !componentKeys.has(item.key));
    return marked.map((item) => {
      const rendered = (state.giftLines || []).find((line) => line.key === item.key);
      return {
        key: item.key,
        variantId: Number(item.variant_id),
        quantity: Number(item.quantity),
        netCents: rendered ? Number(rendered.netCents) : Number(item.final_line_price)
      };
    });
  }

  function isActive(state, config) {
    return Boolean(config.active && state.active && state.country === 'AU' && state.currency === 'AUD');
  }

  function offerAttributeChanges(state, config) {
    const current = state.cart?.attributes || {};
    const desired = isActive(state, config) ? config.attributeValue : (config.controlValue || 'control');
    const changes = {};
    if (current[config.attributeKey] !== desired) changes[config.attributeKey] = desired;
    const legacyKey = config.legacyAttributeKey;
    if (legacyKey && legacyKey !== config.attributeKey && current[legacyKey] != null && current[legacyKey] !== '') {
      changes[legacyKey] = '';
    }
    return changes;
  }

  function shippingRuleValue(state, config) {
    if (state.shippingRuleEnabled !== true) return '';
    let count;
    let route = 'legacy';
    if (state.country === 'AU' && state.currency === 'AUD') {
      route = isActive(state, config) ? 'treatment' : 'control';
      if (!Array.isArray(state.tierLines) || !Array.isArray(state.cart?.items)) throw new Error('Shipping quantity unavailable');
      const eligibility = new Map(state.tierLines.map(line => [line.key, line.eligible === true]));
      const children = new Set(state.cart.items.flatMap(item => (item.item_components || []).map(component => component.key)));
      count = state.cart.items.reduce((total, item) => {
        if (children.has(item.key) || item.parent_relationship) return total;
        if (!eligibility.has(item.key)) throw new Error('Shipping line eligibility unavailable');
        const quantity = Number(item.quantity);
        if (!Number.isSafeInteger(quantity) || quantity < 0) throw new Error('Invalid shipping quantity');
        return total + (eligibility.get(item.key) ? quantity : 0);
      }, 0);
    } else {
      count = Number(state.legacyShippingCount);
    }
    if (!Number.isSafeInteger(count) || count < 0) throw new Error('Invalid shipping quantity');
    return route + '_' + (count >= 3 ? '3plus' : count);
  }

  function cartAttributeChanges(state, config) {
    const changes = { ...offerAttributeChanges(state, config), ...legacyTransitionAttributes(state) };
    const key = config.shippingAttributeKey;
    if (!key) return changes;
    const desired = shippingRuleValue(state, config);
    const current = state.cart?.attributes?.[key];
    if (desired ? current !== desired : current != null && current !== '') changes[key] = desired;
    return changes;
  }

  function isEligible(state, config) {
    return isActive(state, config) && Number(state.spendCents) >= Number(config.giftThreshold || 10000);
  }

  function parseGiftPreference(value, marker) {
    try {
      const record = typeof value === 'string' ? JSON.parse(value) : value;
      if (!record || record.version !== 2 || record.offer !== marker || !Number.isFinite(record.updated)) return null;
      return { version: 2, offer: marker, seen: record.seen === true, declined: record.declined === true,
        variantId: Number.isSafeInteger(record.variantId) && record.variantId > 0 ? record.variantId : 0, updated: record.updated };
    } catch (_) { return null; }
  }

  function shoppingSignature(state, config) {
    return JSON.stringify((state.cart?.items || [])
      .filter(item => !item.properties?.[GIFT_PROPERTY] && Number(item.product_id) !== Number(config.priority?.productId))
      .map(item => [Number(item.variant_id), Number(item.quantity), item.selling_plan_allocation?.selling_plan?.id || 0])
      .sort((a, b) => String(a).localeCompare(String(b))));
  }

  function availableGift(state, preferred = 0, excluded = []) {
    const catalog = state.giftCatalog || state.gifts || [];
    const allowed = (state.allowedGiftVariantIds || catalog.map(gift => gift.variantId)).map(Number);
    const usable = gift => gift.available === true && allowed.includes(Number(gift.variantId)) && !excluded.includes(Number(gift.variantId));
    return catalog.find(gift => Number(gift.variantId) === Number(preferred) && usable(gift)) ||
      (state.gifts || catalog).find(usable);
  }

  function cleanupUpdates(state, config) {
    const lines = giftLines(state, config.giftMarker);
    const variants = Array.isArray(state.allowedGiftVariantIds)
      ? state.allowedGiftVariantIds.map(Number)
      : (state.gifts || []).map((gift) => Number(gift.variantId));
    const canKeep = isEligible(state, config) && state.configured;
    const updates = {};
    let kept = false;
    for (const line of lines) {
      const stock = state.giftCatalog?.find(gift => Number(gift.variantId) === line.variantId);
      if (!canKeep || kept || !variants.includes(line.variantId) || line.netCents !== 0 || stock?.available === false) {
        updates[line.key] = 0;
      } else {
        kept = true;
        if (line.quantity !== 1) updates[line.key] = 1;
      }
    }
    return updates;
  }

  class ThresholdOffer {
    constructor(config, environment = globalThis) {
      this.config = config;
      this.environment = environment;
      this.window = environment.window || environment;
      this.document = environment.document;
      this.queue = Promise.resolve();
      this.keyAliases = new Map();
      this.previousLineQuantities = new Map();
      this.mergedLineKeys = new Set();
      this.lineMergeEvents = [];
      this.mergeRevision = 0;
      this.state = null;
      this.replayForms = new WeakSet();
      this.seen = new Set();
      this.giftPreference = null;
      this.preferenceCartKey = null;
      this.shoppingChange = null;
      this.autoPromptPending = false;
      this.autoPromptTimer = null;
      this.restoreAttempt = '';
      this.giftFollowupReason = '';
      this.checkoutPending = false;
      this.cartNavigationPending = false;
      this.choosing = false;
      this.giftVerificationPending = false;
      this.legacyTransitionPending = false;
      try { this.giftVerificationPending = this.window.sessionStorage.getItem(GIFT_PENDING_KEY) === this.config.giftMarker; } catch (_) {}
      this.refreshTimer = null;
      this.error = '';
      this.dialogPurpose = '';
      this.dialogRequestId = 0;
      this.cartWrites = new Set();
      this.priorityError = '';
      this.priorityLeaving = false;
      this.checkoutLeaving = false;
      this.operationGeneration = 0;
      this.activeOperationGeneration = null;
      this.activeRequests = new Set();
      this.onClick = this.onClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onCartChanged = this.onCartChanged.bind(this);
    }

    start() {
      this.renderCheckoutLog();
      let initial = this.config.initialState;
      if (!initial) {
        try {
          const node = this.document.querySelector('script[data-aerre-threshold-state]');
          if (node) initial = JSON.parse(node.textContent);
        } catch (_) {}
      }
      if (initial) this.observeGiftVerification(initial);
      this.legacyTransitionPending = Boolean(initial && needsLegacyTransition(initial));
      const cleanControl = !this.giftVerificationPending && !this.legacyTransitionPending && initial && !isActive(initial, this.config) && !Object.keys(cartAttributeChanges(initial, this.config)).length && !giftLines(initial, this.config.giftMarker).length && !tierPropertyChanges(initial).length && !priorityLines(initial, this.config).length;
      if (initial) this.state = initial;
      this.document.addEventListener('click', this.onClick, true);
      this.document.addEventListener('submit', this.onSubmit, true);
      this.document.documentElement.addEventListener('cart:updated', this.onCartChanged);
      this.document.documentElement.addEventListener('cart:refresh', this.onCartChanged);
      this.window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
          this.operationGeneration += 1;
          this.checkoutLeaving = false;
          this.checkoutPending = false;
          this.cartNavigationPending = false;
          this.priorityLeaving = false;
          this.priorityError = '';
          this.renderPriority();
          this.refresh(false).catch((error) => {
            if (error.code === 'CHECKOUT_HANDOFF') return;
            this.priorityError = 'prioritySyncFailed';
            this.renderPriority();
            this.showError('syncFailed', undefined, true);
          });
        }
      });
      if (this.config.priority?.enabled && this.environment.MutationObserver) {
        this.priorityObserver = new this.environment.MutationObserver(() => this.renderPriority());
        const drawer = this.document.querySelector('cart-drawer#mini-cart');
        if (drawer) this.priorityObserver.observe(drawer, { childList: true, subtree: true });
      }
      this.document.querySelector('[data-aerre-gift-dialog]')?.addEventListener('click', (event) => {
        if (event.target === event.currentTarget && !this.choosing) {
          this.dialogRequestId += 1;
          event.currentTarget.close();
        }
      });
      this.document.querySelector('[data-aerre-gift-dialog]')?.addEventListener('cancel', (event) => {
        if (this.choosing) event.preventDefault();
        else this.dialogRequestId += 1;
      });
      this.document.querySelector('[data-aerre-gift-dialog]')?.addEventListener('close', () => {
        this.dialogRequestId += 1;
        if (!this.choosing) this.dialogPurpose = '';
        if (!this.dialogTrigger) return;
        const trigger = this.dialogTrigger.isConnected ? this.dialogTrigger : this.document.querySelector('[data-aerre-gift-open]');
        trigger?.focus({ preventScroll: true });
        this.dialogTrigger = null;
      });
      this.ready = cleanControl ? Promise.resolve(initial) : this.refresh(false);
      this.ready.catch((error) => { if (error.code !== 'CHECKOUT_HANDOFF') this.showError('syncFailed', undefined, true); });
      this.renderPriority();
      return this.ready;
    }

    observeGiftVerification(state) {
      if (giftLines(state, this.config.giftMarker).some(line => line.netCents !== 0)) {
        this.setGiftVerificationPending(true);
      }
    }

    setGiftVerificationPending(pending) {
      this.giftVerificationPending = Boolean(pending);
      try {
        if (pending) this.window.sessionStorage.setItem(GIFT_PENDING_KEY, this.config.giftMarker);
        else this.window.sessionStorage.removeItem(GIFT_PENDING_KEY);
      } catch (_) {}
    }

    trackCartWrite(promise) {
      this.cartWrites.add(promise);
      const done = () => this.cartWrites.delete(promise);
      promise.then(done, done);
      return promise;
    }

    async waitForCartWrites() {
      while (this.cartWrites.size) await Promise.allSettled([...this.cartWrites]);
    }

    canContinue(generation = this.activeOperationGeneration ?? this.operationGeneration) {
      return !this.checkoutLeaving && generation === this.operationGeneration;
    }

    assertCurrent(generation) {
      if (this.canContinue(generation)) return;
      const error = new Error('Checkout handoff started');
      error.code = 'CHECKOUT_HANDOFF';
      throw error;
    }

    renderCheckoutLog(record) {
      // Local, capped diagnostics only; never include cart or customer identifiers.
      try {
        const stored = JSON.parse(this.window.sessionStorage.getItem(CHECKOUT_LOG_KEY) || '[]');
        const records = Array.isArray(stored) ? stored.slice(record ? -19 : -20) : [];
        if (record) records.push(record);
        if (record) this.window.sessionStorage.setItem(CHECKOUT_LOG_KEY, JSON.stringify(records));
        let node = this.document.getElementById('aerre-checkout-handoff-log');
        if (!node) {
          node = this.document.createElement('script');
          node.id = 'aerre-checkout-handoff-log';
          node.type = 'application/json';
          this.document.documentElement.appendChild(node);
        }
        node.textContent = JSON.stringify(records);
      } catch (_) {}
    }

    setCartNavigationPending(pending) {
      this.cartNavigationPending = Boolean(pending);
      this.renderPriority();
    }

    renderPriority() {
      if (!this.config.priority?.enabled) return;
      const paused = Boolean(this.document.querySelector('[data-membership-paused-notice]'));
      this.document.querySelectorAll('[data-aerre-priority-panel]').forEach(panel => {
        panel.hidden = false;
        const fallback = panel.parentElement.querySelector('[data-aerre-priority-fallback]');
        if (fallback) fallback.hidden = true;
        panel.querySelectorAll('[data-aerre-priority-checkout]').forEach(button => {
          button.disabled = this.checkoutPending || this.cartNavigationPending || paused || (button.dataset.aerrePriorityCheckout === 'priority' && this.state?.priority?.available === false);
          button.setAttribute('aria-busy', String(this.checkoutPending || this.cartNavigationPending));
        });
        const notice = panel.querySelector('[data-aerre-priority-error]');
        if (notice) {
          const message = this.config.strings?.[this.priorityError] || '';
          if (notice.textContent !== message) notice.textContent = message;
          notice.hidden = !message;
        }
        const price = Number(panel.dataset.price);
        const base = Number(panel.dataset.baseTotal);
        const currency = panel.dataset.currency || this.state?.currency;
        if (!Number.isFinite(price) || !Number.isFinite(base)) return;
        try {
          const formatter = new Intl.NumberFormat(this.document.documentElement.lang || 'en-AU', { style: 'currency', currency });
          const total = panel.querySelector('[data-aerre-priority-total]');
          const fee = panel.querySelector('[data-aerre-priority-price]');
          const totalText = formatter.format((base + price) / 100);
          const feeText = formatter.format(price / 100);
          if (total && total.textContent !== totalText) total.textContent = totalText;
          if (fee && fee.textContent !== feeText) fee.textContent = feeText;
          panel.dataset.price = String(price);
        } catch (_) {}
      });
    }

    async syncPriority(state, selected) {
      if (!this.config.priority?.enabled) return { state, changed: false };
      let changed = false;
      const updates = priorityUpdates(state, this.config, selected);
      if (Object.keys(updates).length) {
        await this.write('update', { updates });
        state = await this.readState();
        changed = true;
      }
      if (selected && !priorityLines(state, this.config).length) {
        let addError;
        try {
          await this.write('add', { items: [{ id: this.config.priority.variantId, quantity: 1, properties: { _aerre_priority_checkout: 'v1' } }] });
        } catch (error) { addError = error; }
        state = await this.readState();
        changed = true;
        if (addError && !priorityLines(state, this.config).length) throw addError;
      }
      const lines = priorityLines(state, this.config);
      const valid = selected ? lines.length === 1 && Number(lines[0].quantity) === 1 && Number(lines[0].variant_id) === Number(this.config.priority.variantId) : lines.length === 0;
      if (!valid) throw new Error('Priority choice could not be confirmed');
      return { state, changed };
    }

    enqueue(operation, checkoutPreparation = false) {
      if (this.checkoutPending && !checkoutPreparation) {
        const error = new Error('Checkout handoff started');
        error.code = 'CHECKOUT_HANDOFF';
        return Promise.reject(error);
      }
      const generation = this.operationGeneration;
      const run = async () => {
        this.assertCurrent(generation);
        this.activeOperationGeneration = generation;
        try {
          const locks = this.window.navigator?.locks;
          if (locks?.request) return await locks.request('aerre-threshold-cart-v2', async () => {
            this.assertCurrent(generation);
            return operation();
          });
          return await operation();
        }
        finally { this.activeOperationGeneration = null; }
      };
      const task = this.queue.then(run, run);
      this.queue = task.catch(() => {});
      return task;
    }

    beforeCartWrite() {
      return this.enqueue(async () => {
        const state = await this.reconcile(false);
        this.rememberShoppingChange(state);
        return state;
      }).catch((error) => {
        if (error.code === 'CHECKOUT_HANDOFF') throw error;
        this.showError('syncFailed', undefined, true);
        throw new Error(this.config.strings?.syncFailed || error.message);
      });
    }

    refresh(shopperChange = true) {
      return this.enqueue(() => this.reconcile(shopperChange));
    }

    async request(url, options = {}, format = 'json') {
      const generation = this.activeOperationGeneration ?? this.operationGeneration;
      this.assertCurrent(generation);
      const controller = new this.environment.AbortController();
      this.activeRequests.add(controller);
      const timeout = this.window.setTimeout(() => controller.abort(), 15000);
      try {
        const response = await this.environment.fetch(url, {
          credentials: 'same-origin',
          cache: 'no-store',
          ...options,
          signal: controller.signal
        });
        if (!response.ok) throw new Error('Cart request failed');
        const result = format === 'text' ? await response.text() : await response.json();
        this.assertCurrent(generation);
        return result;
      } catch (error) {
        this.assertCurrent(generation);
        throw error;
      } finally {
        this.activeRequests.delete(controller);
        this.window.clearTimeout(timeout);
      }
    }

    async readState() {
      const url = new URL(this.config.cartUrl, this.window.location.origin);
      url.searchParams.set('section_id', 'aerre-threshold-state');
      const previewTheme = this.config.previewThemeId || new URL(this.window.location.href).searchParams.get('preview_theme_id');
      if (previewTheme) {
        url.searchParams.set('preview_theme_id', previewTheme);
        url.searchParams.set('_eab', '1');
      }
      const html = await this.request(url.pathname + url.search, {}, 'text');
      const parsed = new this.environment.DOMParser().parseFromString(html, 'text/html');
      const node = parsed.querySelector('[data-aerre-threshold-state]');
      if (!node) throw new Error('Offer state unavailable');
      const state = JSON.parse(node.textContent);
      if (!Array.isArray(state.cart?.items) || !Number.isFinite(Number(state.spendCents)) || !Number.isFinite(Number(state.perfumeCount))) {
        throw new Error('Invalid offer state');
      }
      if (!state.cart.token) {
        const cart = await this.request(`${this.config.cartUrl.replace(/\/$/, '')}.js`);
        if (typeof cart.token !== 'string' || !cart.token) throw new Error('Cart session unavailable');
        state.cart.token = cart.token;
      }
      state.progressTemplates = {};
      parsed.querySelectorAll('template[data-aerre-threshold-progress-template]').forEach((template) => {
        state.progressTemplates[template.dataset.aerreThresholdProgressTemplate] = template.innerHTML;
      });
      const giftOptions = parsed.querySelector('template[data-aerre-gift-options-template]');
      if (giftOptions) state.giftOptionsHtml = giftOptions.innerHTML;
      this.observeGiftVerification(state);
      return state;
    }

    async write(endpoint, payload) {
      return this.request(`${this.config.cartUrl.replace(/\/$/, '')}/${endpoint}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(payload)
      });
    }

    resolveLineKey(key) {
      if (this.state?.cart?.items?.some(item => item.key === key)) return key;
      const seen = new Set();
      while (this.keyAliases.has(key) && !seen.has(key)) {
        seen.add(key);
        key = this.keyAliases.get(key);
      }
      return key;
    }

    resolveLineChange(key, requestedQuantity, previousQuantity, previousMergeRevision) {
      const id = this.resolveLineKey(key);
      const current = this.state?.cart?.items?.find(item => item.key === id);
      const requested = Number(requestedQuantity);
      if (!current || !Number.isFinite(requested)) {
        return { id, quantity: requestedQuantity };
      }
      if (id !== key && this.mergedLineKeys.has(key)) {
        const previous = this.previousLineQuantities.get(key);
        return { id, quantity: Math.max(0, Number(current.quantity) + requested - previous) };
      }
      // Only a confirmed property-induced merge changes the meaning of an
      // absolute quantity request. An ordinary stale cart read must not do so.
      const added = previousMergeRevision == null ? 0 : this.lineMergeEvents
        .filter(event => event.revision > previousMergeRevision && event.key === key)
        .reduce((total, event) => total + event.added, 0);
      return { id, quantity: Math.max(0, requested + added) };
    }

    rememberLineKeys(before, after, update) {
      const nextItems = after?.cart?.items || [];
      for (const item of before?.cart?.items || []) {
        if (item.parent_relationship || nextItems.some(next => next.key === item.key)) continue;
        const properties = update?.id === item.key ? update.properties : item.properties;
        const matches = nextItems.filter(next => !next.parent_relationship &&
          String(next.variant_id || next.id) === String(item.variant_id || item.id) &&
          String(next.selling_plan_allocation?.selling_plan?.id || '') === String(item.selling_plan_allocation?.selling_plan?.id || '') &&
          propertiesMatch(next.properties, properties));
        if (matches.length === 1 && matches[0].key !== item.key) {
          this.keyAliases.set(item.key, matches[0].key);
          this.previousLineQuantities.set(item.key, Number(item.quantity));
          if (update?.id === item.key && Number(matches[0].quantity) > Number(item.quantity)) {
            const mergedOthers = (before.cart.items || []).filter(other =>
              other.key !== item.key && !other.parent_relationship &&
              String(other.variant_id || other.id) === String(item.variant_id || item.id) &&
              String(other.selling_plan_allocation?.selling_plan?.id || '') === String(item.selling_plan_allocation?.selling_plan?.id || '') &&
              propertiesMatch(other.properties, update.properties));
            if (mergedOthers.length) {
              this.mergedLineKeys.add(item.key);
              this.mergeRevision += 1;
              for (const other of mergedOthers) {
                this.lineMergeEvents.push({ revision: this.mergeRevision, key: other.key, added: Number(item.quantity) });
              }
            }
          }
        }
      }
    }

    async removeRetiredGifts(state) {
      let changed = false;
      for (let attempt = 0; attempt < 3 && retiredGiftItems(state).length; attempt += 1) {
        this.legacyTransitionPending = true;
        const memberKeys = new Set(retiredGiftItems(state).filter(item => item.properties?._aerre_rewards_gift).map(item => item.key));
        const lines = giftLines(state, RETIRED_GIFT_MARKER).filter(line => !memberKeys.has(line.key));
        if (!lines.length || lines.some(line => typeof line.key !== 'string' || !line.key)) throw new Error('Previous gift cleanup could not be confirmed');
        let writeError;
        try { await this.write('update', { updates: Object.fromEntries(lines.map(line => [line.key, 0])) }); }
        catch (error) { writeError = error; }
        state = await this.readState();
        changed = true;
        if (writeError && retiredGiftItems(state).length) throw writeError;
      }
      if (retiredGiftItems(state).length) throw new Error('Previous gift cleanup could not be confirmed');
      if (changed) {
        const token = state.cart?.token;
        const key = token ? `aerre:threshold-gift:${String(token).split('?')[0]}` : null;
        if (key) {
          this.seen.delete(key);
          try { this.window.sessionStorage.removeItem(key); } catch (_) {}
        }
      }
      return { state, changed };
    }

    async synchronizeAttributes(state) {
      const migrated = await this.removeRetiredGifts(state);
      state = migrated.state;
      let changed = migrated.changed;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const attributes = cartAttributeChanges(state, this.config);
        if (!Object.keys(attributes).length) return { state, changed };
        const previous = state;
        await this.write('update', { attributes });
        state = await this.readState();
        this.rememberLineKeys(previous, state);
        changed = true;
      }
      if (Object.keys(cartAttributeChanges(state, this.config)).length) throw new Error('Offer assignment could not be confirmed');
      return { state, changed };
    }

    async reconcile(showPopup, keepPriority = this.priorityLeaving) {
      let state = await this.readState();
      this.loadGiftPreference(state);
      const remembered = giftLines(state, this.config.giftMarker).find(line => line.netCents === 0);
      if (remembered && !this.giftPreference.declined) this.updateGiftPreference({ seen: true, variantId: remembered.variantId });
      this.legacyTransitionPending = this.legacyTransitionPending || needsLegacyTransition(state);
      this.rememberLineKeys(this.state, state);
      const migrated = await this.removeRetiredGifts(state);
      state = migrated.state;
      let changed = migrated.changed;
      if (!keepPriority) {
        const cleaned = await this.syncPriority(state, false);
        state = cleaned.state;
        changed = changed || cleaned.changed;
      }
      const assigned = await this.synchronizeAttributes(state);
      state = assigned.state;
      changed = changed || assigned.changed;
      const tierAttempts = (state.cart.items || []).length + 3;
      for (let attempt = 0; attempt < tierAttempts; attempt += 1) {
        const update = tierPropertyChanges(state)[0];
        if (!update) break;
        const previous = state;
        await this.write('change', update);
        state = await this.readState();
        this.rememberLineKeys(previous, state, update);
        changed = true;
      }
      if (tierPropertyChanges(state).length) throw new Error('Tier eligibility could not be confirmed');
      let removed = false;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const updates = cleanupUpdates(state, this.config);
        if (!Object.keys(updates).length) break;
        removed = true;
        await this.write('update', { updates });
        state = await this.readState();
        changed = true;
      }
      if (Object.keys(cleanupUpdates(state, this.config)).length) throw new Error('Gift cleanup could not be confirmed');
      if (this.giftPreference.declined && giftLines(state, this.config.giftMarker).length) {
        state = await this.removeAllGifts();
        changed = true;
      }
      const finalAssignment = await this.synchronizeAttributes(state);
      state = finalAssignment.state;
      changed = changed || finalAssignment.changed;
      if (tierPropertyChanges(state).length || Object.keys(cleanupUpdates(state, this.config)).length) throw new Error('Cart changed during offer assignment');
      if (needsLegacyTransition(state)) throw new Error('Previous offer cleanup could not be confirmed');
      this.legacyTransitionPending = false;
      this.setGiftVerificationPending(false);
      if (!isEligible(state, this.config)) this.restoreAttempt = '';
      const preferred = this.giftPreference.variantId;
      const restore = availableGift(state, preferred);
      const restoreKey = `${this.preferenceCartKey}:${preferred}:${state.spendCents}:${shoppingSignature(state, this.config)}`;
      if (!this.choosing && !this.checkoutPending && isEligible(state, this.config) && !this.giftPreference.declined && preferred &&
          !giftLines(state, this.config.giftMarker).length && Number(restore?.variantId) === preferred && this.restoreAttempt !== restoreKey) {
        this.restoreAttempt = restoreKey;
        const restored = await this.addVerifiedGift(state, preferred);
        state = restored.state;
        changed = true;
        if (restored.added) this.updateGiftPreference({ seen: true, declined: false, variantId: preferred });
      }
      state = await this.persistGiftPreference(state);
      this.setState(state);
      if (changed || this.error === 'syncFailed' || this.error === 'giftVerificationPending') await this.publish(state);
      if (this.error === 'syncFailed' || this.error === 'giftVerificationPending') {
        this.clearError();
        if (this.dialogPurpose === 'error') {
          this.document.querySelector('[data-aerre-gift-dialog]')?.close();
          this.dialogPurpose = '';
          this.status('');
        }
      }
      if (removed && !this.error) this.status('removed');
      const crossed = this.consumeShoppingChange(state);
      if ((showPopup || crossed.navigation) && crossed.eligible) this.queueAutoPrompt();
      return state;
    }

    setState(state) {
      if (!this.canContinue()) return;
      this.state = state;
      if (state.cart) {
        const base = Math.max(0, Number(state.cart.total_price) - priorityLines(state, this.config).reduce((sum, item) => sum + Number(item.final_line_price), 0));
        this.document.querySelectorAll('[data-aerre-priority-panel]').forEach(panel => {
          if (Number.isFinite(base)) panel.dataset.baseTotal = String(base);
          if (state.priority?.price != null) panel.dataset.price = String(state.priority.price);
          if (state.currency) panel.dataset.currency = state.currency;
        });
      }
      this.renderPriority();
      const giftOptions = this.document.querySelector('.aerre-threshold-gift-options');
      let focusedGift;
      if (giftOptions && typeof state.giftOptionsHtml === 'string' && state.giftOptionsHtml !== this.renderedGiftOptionsHtml) {
        const focused = this.document.activeElement;
        if (focused && giftOptions.contains(focused)) focusedGift = focused.closest('[data-aerre-gift-choose]')?.dataset.variantId;
        giftOptions.innerHTML = state.giftOptionsHtml;
        this.renderedGiftOptionsHtml = state.giftOptionsHtml;
      }
      for (const surface of ['cart', 'announcement', 'drawer', 'drawer-gift']) {
        if (!state.progressTemplates?.[surface]) continue;
        const template = this.document.createElement('template');
        template.innerHTML = state.progressTemplates[surface];
        const replacement = template.content.querySelector(`[data-aerre-threshold-progress="${surface}"]`);
        if (!replacement) continue;
        this.document.querySelectorAll(`[data-aerre-threshold-progress="${surface}"]`).forEach((current) => {
          current.replaceWith(replacement.cloneNode(true));
        });
      }
      this.document.querySelectorAll('[data-aerre-gift-cart-status]').forEach((node) => {
        const text = this.config.strings?.[this.cartNotice] || '';
        node.textContent = text;
        node.hidden = !text;
      });
      this.document.documentElement.dispatchEvent(new this.environment.CustomEvent('aerre:threshold-state', {
        bubbles: true,
        detail: { state }
      }));
      const eligible = isEligible(state, this.config);
      const selected = giftLines(state, this.config.giftMarker)[0];
      this.document.querySelectorAll('[data-aerre-gift-choose]').forEach((button) => {
        const gift = state.gifts?.find((entry) => Number(entry.variantId) === Number(button.dataset.variantId));
        button.disabled = this.choosing || !eligible || !state.configured || !gift?.available;
        button.setAttribute('aria-pressed', String(Number(selected?.variantId) === Number(button.dataset.variantId)));
        if (focusedGift === button.dataset.variantId && !button.disabled) button.focus({ preventScroll: true });
      });
      if (isActive(state, this.config) && this.dialogPurpose === 'gift' && !this.error && !eligible && this.document.querySelector('[data-aerre-gift-dialog]')?.open && !this.checkoutPending) {
        this.status('notEligible');
      }
    }

    sectionTargets() {
      const targets = [];
      const drawer = this.document.querySelector('cart-drawer#mini-cart');
      if (drawer) targets.push({ id: drawer.getAttribute('section') || 'mini-cart', drawer: true });
      const page = this.document.querySelector('[data-aerre-rewards-cart-section]')?.closest('[id^="shopify-section-"]');
      if (page) targets.push({ id: page.id.replace('shopify-section-', ''), element: page });
      return targets;
    }

    async publish(state) {
      const targets = this.sectionTargets();
      if (targets.length) {
        const url = new URL(this.window.location.href);
        url.searchParams.delete('section_id');
        url.searchParams.set('sections', targets.map((target) => target.id).join(','));
        const sections = await this.request(url.pathname + url.search);
        for (const target of targets) {
          if (typeof sections[target.id] !== 'string') throw new Error('Cart section unavailable');
          if (!target.drawer) {
            const parsed = new this.environment.DOMParser().parseFromString(sections[target.id], 'text/html');
            const replacement = parsed.getElementById(`shopify-section-${target.id}`);
            if (!replacement) throw new Error('Cart section unavailable');
            target.element.replaceChildren(...replacement.childNodes);
          }
        }
        const drawer = targets.find((target) => target.drawer);
        if (drawer) sections['mini-cart'] = sections[drawer.id];
        state.cart.sections = sections;
      }
      for (const name of ['cart:updated', 'cart:refresh']) {
        this.document.documentElement.dispatchEvent(new this.environment.CustomEvent(name, {
          bubbles: true,
          detail: { cart: state.cart, aerreThresholdOffer: true, replacementDelay: 1 }
        }));
      }
      this.setState(state);
    }

    sessionKey() {
      const token = this.state?.cart?.token;
      return token ? `aerre:threshold-gift:${String(token).split('?')[0]}` : null;
    }

    preferenceKey(state = this.state) {
      const token = state?.cart?.token;
      return token ? `aerre:gwp:v2:${this.config.giftMarker}:${String(token).split('?')[0]}` : null;
    }

    loadGiftPreference(state) {
      const key = this.preferenceKey(state);
      if (key !== this.preferenceCartKey) { this.giftPreference = null; this.preferenceCartKey = key; }
      let local;
      try { local = key && this.window.localStorage?.getItem(key); } catch (_) {}
      const candidates = [this.giftPreference, local, state.cart?.attributes?.[GIFT_PREFERENCE_ATTRIBUTE]]
        .map(value => parseGiftPreference(value, this.config.giftMarker)).filter(Boolean).sort((a, b) => b.updated - a.updated);
      this.giftPreference = candidates[0] || { version: 2, offer: this.config.giftMarker, seen: false, declined: false, variantId: 0, updated: 0 };
      if (candidates.some(record => record.seen)) this.giftPreference.seen = true;
      this.cacheGiftPreference();
      return this.giftPreference;
    }

    cacheGiftPreference() {
      if (!this.preferenceCartKey || !this.giftPreference?.updated) return;
      try { this.window.localStorage?.setItem(this.preferenceCartKey, JSON.stringify(this.giftPreference)); } catch (_) {}
    }

    updateGiftPreference(changes) {
      if (!this.giftPreference) this.loadGiftPreference(this.state || {});
      if (Object.entries(changes).every(([key, value]) => this.giftPreference[key] === value)) return;
      this.giftPreference = { ...this.giftPreference, ...changes, updated: Math.max(Date.now(), this.giftPreference.updated + 1) };
      this.cacheGiftPreference();
    }

    async persistGiftPreference(state) {
      if (!this.giftPreference?.updated || !this.preferenceKey(state)) return state;
      const value = JSON.stringify(this.giftPreference);
      if (state.cart.attributes?.[GIFT_PREFERENCE_ATTRIBUTE] === value) return state;
      let cart;
      try { cart = await this.write('update', { attributes: { [GIFT_PREFERENCE_ATTRIBUTE]: value } }); }
      catch (_) {
        state = await this.readState();
        cart = state.cart;
      }
      if (cart?.attributes?.[GIFT_PREFERENCE_ATTRIBUTE] !== value) throw new Error('Gift preference could not be saved');
      state.cart.attributes = { ...state.cart.attributes, [GIFT_PREFERENCE_ATTRIBUTE]: value };
      return state;
    }

    rememberShoppingChange(state) {
      this.shoppingChange = { key: this.preferenceKey(state), eligible: isEligible(state, this.config),
        signature: shoppingSignature(state, this.config), created: Date.now(), navigation: this.window.themeVariables?.settings?.pageType === 'cart' || this.nativeCartNavigation === true };
      try { this.window.sessionStorage?.setItem('aerre:gwp:shopping-change:v2', JSON.stringify(this.shoppingChange)); } catch (_) {}
    }

    consumeShoppingChange(state) {
      let before = this.shoppingChange;
      if (!before) {
        try { before = JSON.parse(this.window.sessionStorage?.getItem('aerre:gwp:shopping-change:v2') || 'null'); } catch (_) {}
      }
      if (!before || before.key !== this.preferenceKey(state)) return { eligible: false, navigation: false };
      if (Date.now() - before.created < GIFT_INTERACTION_MS && before.signature === shoppingSignature(state, this.config)) return { eligible: false, navigation: false };
      this.shoppingChange = null;
      try { this.window.sessionStorage?.removeItem('aerre:gwp:shopping-change:v2'); } catch (_) {}
      return { eligible: Date.now() - before.created < GIFT_INTERACTION_MS && !before.eligible && isEligible(state, this.config), navigation: before.navigation === true };
    }

    wasSeen() {
      const key = this.sessionKey();
      if (!key || this.giftPreference?.seen || this.giftPreference?.declined || this.seen.has(key)) return true;
      try { return this.window.sessionStorage.getItem(key) === '1'; } catch (_) { return false; }
    }

    markSeen() {
      const key = this.sessionKey();
      if (!key) return;
      this.seen.add(key);
      this.updateGiftPreference({ seen: true });
      try { this.window.sessionStorage.setItem(key, '1'); } catch (_) {}
    }

    hasBlockingPopup() {
      // Alia's collapsed launcher keeps the same root ID as its modal. Only
      // modal semantics should defer the gift prompt, never the launcher tab.
      return Array.from(this.document.querySelectorAll('dialog[open], [role="dialog"], [aria-modal="true"], popup-content[open]')).some(node => {
        if (node.matches?.('[data-aerre-gift-dialog], cart-drawer, #mini-cart') || node.closest?.('#mini-cart')) return false;
        if (node.hidden || node.getAttribute?.('aria-hidden') === 'true') return false;
        const rect = node.getBoundingClientRect();
        const style = this.window.getComputedStyle?.(node);
        return rect.width > 0 && rect.height > 0 && style?.visibility !== 'hidden' && style?.display !== 'none';
      });
    }

    queueAutoPrompt() {
      if (this.wasSeen() || this.checkoutPending || this.choosing || this.autoPromptPending) return;
      this.autoPromptPending = true;
      const expires = Date.now() + 30000;
      const attempt = () => {
        if (!this.autoPromptPending || !this.canContinue() || this.checkoutPending || Date.now() > expires) { this.autoPromptPending = false; return; }
        if (this.hasBlockingPopup()) { this.autoPromptTimer = this.window.setTimeout(attempt, 400); return; }
        this.autoPromptPending = false;
        void this.enqueue(async () => {
          const state = await this.reconcile(false);
          if (this.error || this.checkoutPending || this.choosing || !isEligible(state, this.config) ||
              giftLines(state, this.config.giftMarker).length || this.wasSeen() || !availableGift(state) || this.hasBlockingPopup()) return;
          this.markSeen();
          await this.persistGiftPreference(state);
          if (this.checkoutPending || this.hasBlockingPopup()) return;
          this.status('');
          this.showDialog();
        }).catch(error => { if (error.code !== 'CHECKOUT_HANDOFF') this.showError('syncFailed', undefined, true); });
      };
      this.autoPromptTimer = this.window.setTimeout(attempt, 150);
    }

    showDialog(purpose = 'gift', generation = this.activeOperationGeneration ?? this.operationGeneration) {
      if (!this.canContinue(generation) || this.checkoutPending) return;
      this.dialogPurpose = purpose;
      const dialog = this.document.querySelector('[data-aerre-gift-dialog]');
      if (dialog && !dialog.open) dialog.showModal();
    }

    status(key) {
      const text = this.config.strings?.[key] || '';
      this.document.querySelectorAll('[data-aerre-gift-status]').forEach((node) => {
        node.textContent = text;
        node.hidden = !text;
      });
      if (key === 'removed' || key === 'added' || key === 'declined') {
        this.cartNotice = key;
        this.document.querySelectorAll('[data-aerre-gift-cart-status]').forEach((node) => {
          node.textContent = text;
          node.hidden = !text;
        });
      }
    }

    showError(key, generation = this.activeOperationGeneration ?? this.operationGeneration, silent = false) {
      if (!this.canContinue(generation) || this.checkoutPending) return;
      this.error = key;
      this.status(key);
      this.cartNotice = key;
      this.document.querySelectorAll('[data-aerre-gift-cart-status]').forEach(node => {
        node.textContent = this.config.strings?.[key] || '';
        node.hidden = !node.textContent;
      });
      this.document.querySelectorAll('[data-aerre-gift-retry]').forEach((button) => { button.hidden = false; });
      if (!silent) this.showDialog('error', generation);
    }

    clearError() {
      if (this.cartNotice === this.error) this.cartNotice = '';
      this.error = '';
      this.document.querySelectorAll('[data-aerre-gift-retry]').forEach((button) => { button.hidden = true; });
    }

    async retryDialog() {
      if (this.error && this.error !== 'syncFailed' && this.state && isActive(this.state, this.config)) {
        return this.openGiftChooser();
      }
      try {
        await this.enqueue(() => this.reconcile(false));
      } catch (_) {
        this.showError('syncFailed');
      }
    }

    async openGiftChooser() {
      if (!this.config.active || (this.state && !isActive(this.state, this.config))) return;
      const dialogRequest = ++this.dialogRequestId;
      this.clearError();
      this.showDialog();
      this.status('loading');
      try {
        const state = await this.enqueue(() => this.reconcile(false));
        if (dialogRequest !== this.dialogRequestId) return;
        if (!isActive(state, this.config)) {
          this.document.querySelector('[data-aerre-gift-dialog]')?.close();
          this.dialogPurpose = '';
          return;
        }
        if (!isEligible(state, this.config)) this.status('notEligible');
        else if (!state.configured || !state.gifts?.some((gift) => gift.available)) this.status('unavailable');
        else {
          this.status('');
          this.markSeen();
          await this.enqueue(() => this.persistGiftPreference(state));
        }
      } catch (_) {
        if (dialogRequest !== this.dialogRequestId) return;
        this.showError(this.giftVerificationPending ? 'giftVerificationPending' : 'syncFailed');
      }
    }

    async removeAllGifts() {
      let state = await this.readState();
      const lines = giftLines(state, this.config.giftMarker);
      if (lines.length) {
        await this.write('update', { updates: Object.fromEntries(lines.map((line) => [line.key, 0])) });
        state = await this.readState();
      }
      if (giftLines(state, this.config.giftMarker).length) throw new Error('Gift removal could not be confirmed');
      this.setGiftVerificationPending(false);
      return state;
    }

    async addVerifiedGift(state, variantId) {
      this.setGiftVerificationPending(true);
      let addError;
      try {
        await this.write('add', { items: [{ id: Number(variantId), quantity: 1, properties: { [GIFT_PROPERTY]: this.config.giftMarker } }] });
      } catch (error) { addError = error; }
      // A failed/lost response does not prove the add failed. Read back before
      // retrying, removing, or allowing checkout. Never repeat an uncertain add.
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try { state = await this.readState(); break; }
        catch (error) { if (attempt === 1) throw error; }
      }
      const lines = giftLines(state, this.config.giftMarker);
      const allowed = (state.allowedGiftVariantIds || []).map(Number);
      const confirmed = isEligible(state, this.config) && state.configured && allowed.includes(Number(variantId)) &&
        lines.length === 1 && lines[0].variantId === Number(variantId) && lines[0].quantity === 1 && lines[0].netCents === 0;
      if (confirmed) {
        this.setGiftVerificationPending(false);
        return { state, added: true };
      }
      if (lines.length) state = await this.removeAllGifts();
      this.setGiftVerificationPending(false);
      return { state, added: false, reason: addError ? 'unavailable' : 'chargedGift' };
    }

    async setGiftFollowup(reason) {
      const message = reason ? 'Free 10ml gift due: automatic selection was not confirmed. Check the order for an existing free gift, then add ONE available eligible 10ml gift before dispatch. Reason: ' + reason : '';
      // A private cart attribute is copied into order Additional details without
      // displaying operational instructions to the shopper.
      await this.write('update', { attributes: { [GIFT_FOLLOWUP_ATTRIBUTE]: message } });
      this.giftFollowupReason = reason;
    }

    async prepareCheckoutGift() {
      let state = await this.reconcile(false, false);
      this.loadGiftPreference(state);
      if (!isEligible(state, this.config) || this.giftPreference.declined) {
        await this.setGiftFollowup('');
        return this.giftPreference.declined ? 'declined' : 'not-eligible';
      }
      if (giftLines(state, this.config.giftMarker).length) {
        await this.setGiftFollowup('');
        return 'existing-gift';
      }
      // Set the fulfilment fallback BEFORE attempting an add. If a confirmed
      // stock/discount failure occurs, checkout can continue with this flag.
      await this.setGiftFollowup('gift unavailable or could not be added');
      const attempted = [];
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const candidate = availableGift(state, this.giftPreference.variantId, attempted);
        if (!candidate) break;
        attempted.push(Number(candidate.variantId));
        const result = await this.addVerifiedGift(state, candidate.variantId);
        state = result.state;
        if (result.added) {
          this.updateGiftPreference({ seen: true, declined: false, variantId: Number(candidate.variantId) });
          state = await this.persistGiftPreference(state);
          this.setState(state);
          await this.setGiftFollowup('');
          return 'gift-added';
        }
        if (!isEligible(state, this.config)) {
          await this.setGiftFollowup('');
          this.setState(state);
          return 'not-eligible';
        }
        // A price/discount failure is not fixed by trying more fragrances.
        if (result.reason === 'chargedGift') break;
      }
      if (giftLines(state, this.config.giftMarker).length || this.giftVerificationPending) throw new Error('Gift safety check incomplete');
      this.setState(state);
      return 'fulfilment-followup';
    }

    chooseGift(variantId) {
      if (this.choosing) return this.queue;
      this.autoPromptPending = false;
      this.clearError();
      this.dialogPurpose = 'gift';
      this.choosing = true;
      if (this.state) this.setState(this.state);
      this.status('choosing');
      return this.enqueue(async () => {
        let failureKey = 'addFailed';
        try {
          let state = await this.reconcile(false);
          const variant = (state.giftCatalog || state.gifts || []).find(gift => Number(gift.variantId) === Number(variantId));
          if (!isEligible(state, this.config)) { failureKey = 'notEligible'; throw new Error('Gift not eligible'); }
          if (!state.configured || !variant?.available) { failureKey = 'availabilityChanged'; throw new Error('Gift unavailable'); }
          const existing = giftLines(state, this.config.giftMarker);
          if (!(existing.length === 1 && existing[0].variantId === Number(variantId) && existing[0].netCents === 0)) {
            state = await this.removeAllGifts();
            const result = await this.addVerifiedGift(state, variantId);
            state = result.state;
            if (!result.added) { failureKey = result.reason; throw new Error('Gift could not be confirmed'); }
          }
          this.updateGiftPreference({ seen: true, declined: false, variantId: Number(variantId) });
          state = await this.persistGiftPreference(state);
          await this.setGiftFollowup('');
          this.clearError();
          this.setState(state);
          await this.publish(state);
          this.markSeen();
          this.status('added');
          this.document.querySelector('[data-aerre-gift-dialog]')?.close();
        } catch (_) {
          if (this.canContinue()) this.showError(this.giftVerificationPending ? 'giftVerificationPending' : failureKey);
        } finally {
          this.choosing = false;
          if (this.state) this.setState(this.state);
        }
      });
    }

    declineGift() {
      if (this.choosing || this.checkoutPending) return this.queue;
      this.autoPromptPending = false;
      this.choosing = true;
      return this.enqueue(async () => {
        try {
          let state = await this.readState();
          this.loadGiftPreference(state);
          this.updateGiftPreference({ seen: true, declined: true });
          state = await this.persistGiftPreference(state);
          state = await this.removeAllGifts();
          await this.setGiftFollowup('');
          this.clearError();
          this.setState(state);
          await this.publish(state);
          this.status('declined');
          this.document.querySelector('[data-aerre-gift-dialog]')?.close();
        } catch (_) {
          this.showError(this.giftVerificationPending ? 'giftVerificationPending' : 'removeFailed', undefined, true);
        } finally {
          this.choosing = false;
          if (this.state) this.setState(this.state);
        }
      });
    }

    onCartChanged(event) {
      if (event.detail?.aerreThresholdOffer || this.checkoutPending || this.cartNavigationPending) return;
      this.window.clearTimeout(this.refreshTimer);
      this.refreshTimer = this.window.setTimeout(() => {
        this.refresh().catch((error) => { if (error.code !== 'CHECKOUT_HANDOFF') this.showError('syncFailed', undefined, true); });
      }, 30);
    }

    block(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    async prepareCheckoutPriority(selected, displayedPrice, attempt) {
      attempt.stage = 'cart-read';
      let cart = await this.request(`${this.config.cartUrl.replace(/\/$/, '')}.js`);
      if (!Array.isArray(cart.items)) throw new Error('Cart unavailable');
      let state = { cart };
      this.legacyTransitionPending = this.legacyTransitionPending || needsLegacyTransition(state);
      this.observeGiftVerification(state);
      if (this.giftVerificationPending || this.legacyTransitionPending) {
        attempt.stage = this.legacyTransitionPending ? 'previous-offer-cleanup' : 'pending-gift-verification';
        state = await this.reconcile(false);
        if (this.giftVerificationPending || this.legacyTransitionPending) throw new Error('Gift or previous offer verification pending');
        cart = state.cart;
      }
      if (!this.config.priority?.enabled) return;
      const rememberPriority = () => {
        const lines = priorityLines(state, this.config);
        attempt.knownPriority = !lines.length ? 'absent' :
          lines.length === 1 && Number(lines[0].quantity) === 1 && Number(lines[0].variant_id) === Number(this.config.priority.variantId) ? 'present' : 'other';
      };
      rememberPriority();
      if (selected && (!this.state?.priority?.available ||
          (Number.isFinite(displayedPrice) && displayedPrice !== Number(this.state.priority.price)) ||
          this.document.querySelector('[data-membership-paused-notice]') ||
          !cart.items.some(item => Number(item.product_id) !== Number(this.config.priority.productId) && !item.properties?._aerre_threshold_gift && !item.properties?._aerre_rewards_gift))) {
        throw new Error('Priority unavailable');
      }
      const updates = priorityUpdates(state, this.config, selected);
      if (Object.keys(updates).length) {
        attempt.stage = 'priority-update';
        attempt.knownPriority = 'unknown';
        cart = await this.write('update', { updates });
        if (!Array.isArray(cart.items)) throw new Error('Cart unavailable');
        state = { cart };
        rememberPriority();
      }
      if (selected && !priorityLines(state, this.config).length) {
        attempt.stage = 'priority-add';
        attempt.addAttempted = true;
        attempt.knownPriority = 'unknown';
        // A lost response does not prove failure. Never repeat or undo this add.
        await this.write('add', { items: [{ id: this.config.priority.variantId, quantity: 1, properties: { _aerre_priority_checkout: 'v1' } }] });
        attempt.stage = 'priority-confirm';
        cart = await this.request(`${this.config.cartUrl.replace(/\/$/, '')}.js`);
        if (!Array.isArray(cart.items)) throw new Error('Cart unavailable');
        state = { cart };
        rememberPriority();
      }
      if (attempt.knownPriority !== (selected ? 'present' : 'absent')) throw new Error('Priority choice unconfirmed');
    }

    async checkout(resume, priority = false) {
      if (this.checkoutPending || this.cartNavigationPending) return;
      this.checkoutPending = true;
      this.autoPromptPending = false;
      this.window.clearTimeout(this.autoPromptTimer);
      this.priorityError = '';
      const generation = this.operationGeneration;
      const now = () => this.window.performance?.now() ?? Date.now();
      const started = now();
      const attempt = { selected: priority ? 'priority' : 'standard', stage: 'pending-cart', knownPriority: 'unknown', addAttempted: false };
      const displayedPrice = Number(this.document.querySelector('[data-aerre-priority-panel]')?.dataset.price);
      this.renderPriority();
      this.window.clearTimeout(this.refreshTimer);
      let deadline;
      try {
        await this.waitForCartWrites();
        this.assertCurrent(generation);
        attempt.stage = 'gift-preparation';
        attempt.gift = await this.enqueue(() => this.prepareCheckoutGift(), true);
      } catch (error) {
        if (error.code === 'CHECKOUT_HANDOFF') return;
        this.checkoutPending = false;
        this.priorityLeaving = false;
        this.showError(this.giftVerificationPending ? 'giftVerificationPending' : 'syncFailed', generation, true);
        this.renderPriority();
        this.renderCheckoutLog({ ...attempt, status: 'gift-verification-required', durationMs: Math.round(now() - started) });
        return;
      }
      const preparation = (async () => {
        await this.waitForCartWrites();
        this.assertCurrent(generation);
        attempt.stage = 'pending-sync';
        await this.enqueue(async () => {
          if (this.giftVerificationPending || this.legacyTransitionPending) {
            attempt.stage = this.legacyTransitionPending ? 'previous-offer-cleanup' : 'pending-gift-verification';
            await this.reconcile(false);
            if (this.giftVerificationPending || this.legacyTransitionPending) throw new Error('Gift or previous offer verification pending');
          }
          this.priorityLeaving = true;
          await this.prepareCheckoutPriority(priority, displayedPrice, attempt);
        }, true);
      })().then(() => 'prepared', () => 'fallback');
      const status = await Promise.race([
        preparation,
        new Promise(resolve => { deadline = this.window.setTimeout(() => resolve('timeout'), CHECKOUT_DEADLINE_MS); })
      ]);
      this.window.clearTimeout(deadline);
      if (this.giftVerificationPending || this.legacyTransitionPending) {
        this.operationGeneration += 1;
        for (const controller of this.activeRequests) controller.abort();
        this.checkoutPending = false;
        this.priorityLeaving = false;
        this.renderPriority();
        this.showError(this.legacyTransitionPending ? 'syncFailed' : 'giftVerificationPending', this.operationGeneration);
        this.renderCheckoutLog({ ...attempt, status: this.legacyTransitionPending ? 'previous-offer-cleanup-required' : 'gift-verification-required', durationMs: Math.round(now() - started), deadlineMs: CHECKOUT_DEADLINE_MS });
        return;
      }
      // Stop old queue continuations before navigating. Aborting a request cannot
      // undo a write already received by Shopify; checkout uses that actual cart.
      this.checkoutLeaving = true;
      this.operationGeneration += 1;
      this.window.clearTimeout(this.refreshTimer);
      for (const controller of this.activeRequests) controller.abort();
      this.renderCheckoutLog({ ...attempt, status, durationMs: Math.round(now() - started), deadlineMs: CHECKOUT_DEADLINE_MS });
      try { resume(); }
      catch (_) {
        this.window.location.assign(`${this.config.rootUrl.replace(/\/$/, '')}/checkout`);
      }
    }

    onClick(event) {
      const target = event.target?.closest ? event.target : event.target?.parentElement;
      if (!target) return;
      const priorityButton = target.closest('[data-aerre-priority-checkout]');
      if (priorityButton) {
        this.block(event);
        if (!priorityButton.disabled) void this.checkout(() => this.window.location.assign(`${this.config.rootUrl.replace(/\/$/, '')}/checkout`), priorityButton.dataset.aerrePriorityCheckout === 'priority');
        return;
      }
      if ((this.checkoutPending || this.cartNavigationPending) && target.closest('line-item-quantity, [data-aerre-gift-open], [data-aerre-gift-choose], [data-aerre-gift-retry], [data-aerre-gift-decline]')) {
        this.block(event);
        return;
      }
      const removeGift = target.closest('.line-item--threshold-gift .line-item__remove-button');
      if (removeGift || target.closest('[data-aerre-gift-decline]')) {
        this.block(event);
        void this.declineGift();
        return;
      }
      const chooser = target.closest('[data-aerre-gift-open]');
      if (target.closest('[data-aerre-gift-retry]')) {
        this.block(event);
        void this.retryDialog();
        return;
      }
      if (chooser) {
        this.block(event);
        this.dialogTrigger = chooser;
        void this.openGiftChooser();
        return;
      }
      if (target.closest('[data-aerre-gift-close]')) {
        this.block(event);
        if (!this.choosing) {
          this.dialogRequestId += 1;
          this.document.querySelector('[data-aerre-gift-dialog]')?.close();
        }
        return;
      }
      const choice = target.closest('[data-aerre-gift-choose]');
      if (choice) {
        this.block(event);
        if (!choice.disabled) void this.chooseGift(choice.dataset.variantId);
        return;
      }
      const link = target.closest('a[href]');
      if (link) {
        const url = new URL(link.href, this.window.location.href);
        if (url.origin === this.window.location.origin && /\/checkouts?(?:\/|$)/.test(url.pathname)) {
          this.block(event);
          void this.checkout(() => this.window.location.assign(url.href));
        }
      }
    }

    onSubmit(event) {
      const form = event.target;
      if (this.replayForms.has(form)) {
        this.replayForms.delete(form);
        return;
      }
      const action = new URL(form.action || this.window.location.href, this.window.location.href);
      const checkout = event.submitter?.name === 'checkout' || form.querySelector('input[name="checkout"]') || /\/checkouts?(?:\/|$)/.test(action.pathname);
      const nativeProductForm = !form.matches('form[is="product-form"]') || this.window.themeVariables?.settings?.cartType === 'page' || this.window.themeVariables?.settings?.pageType === 'cart';
      const nativeCartWrite = nativeProductForm && /\/cart\/(add|change|update)(?:\.js)?\/?$/.test(action.pathname);
      if (!checkout && !nativeCartWrite) return;
      this.block(event);
      if (this.checkoutPending || this.cartNavigationPending) return;
      const submitter = event.submitter;
      const resume = () => {
        const currentForm = form.isConnected ? form : (form.id ? this.document.getElementById(form.id) : null);
        if (!currentForm) {
          throw new Error('Checkout form unavailable');
        }
        const currentSubmitter = submitter?.form === currentForm ? submitter : Array.from(currentForm.elements).find((element) => element.type === 'submit' && element.name === submitter?.name && element.value === submitter?.value);
        this.replayForms.add(currentForm);
        if (currentSubmitter && !currentSubmitter.disabled) currentForm.requestSubmit(currentSubmitter);
        else currentForm.requestSubmit();
      };
      if (checkout) void this.checkout(() => this.window.location.assign(`${this.config.rootUrl.replace(/\/$/, '')}/checkout`));
      else {
        this.nativeCartNavigation = true;
        const generation = this.operationGeneration;
        void this.beforeCartWrite().then(() => { this.assertCurrent(generation); resume(); }).catch((error) => {
          if (error.code !== 'CHECKOUT_HANDOFF') this.showError('syncFailed');
        });
      }
    }
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { ThresholdOffer, parseGiftPreference, shoppingSignature, availableGift, giftLines, isActive, isEligible, cleanupUpdates, tierPropertyChanges, priorityLines, priorityUpdates, shippingRuleValue, cartAttributeChanges, retiredGiftItems, legacyTransitionAttributes, needsLegacyTransition };
  } else {
    const node = document.getElementById('aerre-threshold-config');
    if (!node || node.dataset.started) return;
    node.dataset.started = 'true';
    try {
      const controller = new ThresholdOffer(JSON.parse(node.textContent));
      window.aerreThresholdOffer = controller;
      void controller.start();
    } catch (_) {
      document.querySelectorAll('[data-aerre-gift-status]').forEach((status) => {
        status.hidden = false;
      });
    }
  }
})();


